
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {NewsArticleGetResponse} from './NewsArticleGetResponse';
import {NewsServiceId} from '../core/NewsServiceId';
import {FieldModelNewsServiceId} from '../core/NewsServiceId';
import {TradingCentralArticleQuery} from './TradingCentralArticleQuery';
import {FieldModelTradingCentralArticleQuery} from './TradingCentralArticleQuery';

/**
 * NewsArticleGetRequest struct
 */
class NewsArticleGetRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!NewsServiceId=} newsServiceId
   * @param {TradingCentralArticleQuery=} tradingCentralQuery
   * @constructor
   */
  constructor (parent = new Request(), newsServiceId = new NewsServiceId(), tradingCentralQuery = null) {
    super()
    super.copy(parent)
    this.NewsServiceId = newsServiceId
    this.TradingCentralQuery = tradingCentralQuery
  }

  /**
   * Copy struct (shallow copy)
   * @this {!NewsArticleGetRequest}
   * @param {!NewsArticleGetRequest} other Other struct
   * @returns {!NewsArticleGetRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.NewsServiceId != null) {
      this.NewsServiceId = NewsServiceId.fromObject(other.NewsServiceId)
    } else {
      this.NewsServiceId = null
    }
    if (other.TradingCentralQuery != null) {
      this.TradingCentralQuery = TradingCentralArticleQuery.fromObject(other.TradingCentralQuery)
    } else {
      this.TradingCentralQuery = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!NewsArticleGetRequest}
   * @returns {!NewsArticleGetRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new NewsArticleGetRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new NewsArticleGetRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!NewsArticleGetRequest}
   * @param {!NewsArticleGetRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof NewsArticleGetRequest)) {
      throw new TypeError('Instance of NewsArticleGetRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!NewsArticleGetRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      NewsServiceId: ((this.NewsServiceId != null) ? this.NewsServiceId : null),
      TradingCentralQuery: ((this.TradingCentralQuery != null) ? this.TradingCentralQuery : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return NewsArticleGetRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!NewsArticleGetRequest} other Object value
   * @returns {!NewsArticleGetRequest} Created struct
   */
  static fromObject (other) {
    return new NewsArticleGetRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!NewsArticleGetRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return NewsArticleGetRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!NewsArticleGetRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5214
  }
}

export { NewsArticleGetRequest };

/**
 * Fast Binary Encoding NewsArticleGetRequest field model
 */
class FieldModelNewsArticleGetRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.NewsServiceId = new FieldModelNewsServiceId(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.TradingCentralQuery = new fbe.FieldModelOptional(new FieldModelTradingCentralArticleQuery(buffer, this.NewsServiceId.fbeOffset + this.NewsServiceId.fbeSize), buffer, this.NewsServiceId.fbeOffset + this.NewsServiceId.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelNewsArticleGetRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelNewsArticleGetRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelNewsArticleGetRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.NewsServiceId.fbeSize + this.TradingCentralQuery.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelNewsArticleGetRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.NewsServiceId.fbeExtra + this.TradingCentralQuery.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelNewsArticleGetRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelNewsArticleGetRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelNewsArticleGetRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5214
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelNewsArticleGetRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelNewsArticleGetRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.NewsServiceId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewsServiceId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewsServiceId.fbeSize

    if ((fbeCurrentSize + this.TradingCentralQuery.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.TradingCentralQuery.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TradingCentralQuery.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelNewsArticleGetRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelNewsArticleGetRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelNewsArticleGetRequest}
   * @param {!NewsArticleGetRequest} fbeValue Default value, defaults is new NewsArticleGetRequest()
   * @returns {!NewsArticleGetRequest} NewsArticleGetRequest value
   */
  get (fbeValue = new NewsArticleGetRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelNewsArticleGetRequest}
   * @param {!NewsArticleGetRequest} fbeValue NewsArticleGetRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.NewsServiceId.fbeSize) <= fbeStructSize) {
      fbeValue.NewsServiceId = this.NewsServiceId.get()
    } else {
      fbeValue.NewsServiceId = new NewsServiceId()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewsServiceId.fbeSize

    if ((fbeCurrentSize + this.TradingCentralQuery.fbeSize) <= fbeStructSize) {
      fbeValue.TradingCentralQuery = this.TradingCentralQuery.get()
    } else {
      fbeValue.TradingCentralQuery = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TradingCentralQuery.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelNewsArticleGetRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelNewsArticleGetRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelNewsArticleGetRequest}
   * @param {!NewsArticleGetRequest} fbeValue NewsArticleGetRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelNewsArticleGetRequest}
   * @param {NewsArticleGetRequest} fbeValue NewsArticleGetRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.NewsServiceId.set(fbeValue.NewsServiceId)
    this.TradingCentralQuery.set(fbeValue.TradingCentralQuery)
  }
}

export { FieldModelNewsArticleGetRequest };

/**
 * Fast Binary Encoding NewsArticleGetRequest model
 */
class NewsArticleGetRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelNewsArticleGetRequest(this.buffer, 4)
  }

  /**
   * Get the NewsArticleGetRequest model
   * @this {!NewsArticleGetRequestModel}
   * @returns {!FieldModelNewsArticleGetRequest} model NewsArticleGetRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!NewsArticleGetRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!NewsArticleGetRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return NewsArticleGetRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!NewsArticleGetRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelNewsArticleGetRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!NewsArticleGetRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!NewsArticleGetRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!NewsArticleGetRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!NewsArticleGetRequestModel}
   * @param {!NewsArticleGetRequest} value NewsArticleGetRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!NewsArticleGetRequestModel}
   * @param {!NewsArticleGetRequest} value NewsArticleGetRequest value, defaults is new NewsArticleGetRequest()
   * @return {!object} Deserialized NewsArticleGetRequest value and its size
   */
  deserialize (value = new NewsArticleGetRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new NewsArticleGetRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new NewsArticleGetRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!NewsArticleGetRequestModel}
   * @param {!number} prev Previous NewsArticleGetRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { NewsArticleGetRequestModel };
NewsArticleGetRequest.__has_response = true;
NewsArticleGetRequest.__response_class = NewsArticleGetResponse;
